import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Save, Loader2, Plus, Trash2, RefreshCcw, AlertTriangle } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import IconPicker from "../../components/admin/IconPicker";

const DEFAULT_HOURS = [
  { day: "Lundi", morning: "", afternoon: "", closed: false },
  { day: "Mardi", morning: "", afternoon: "", closed: false },
  { day: "Mercredi", morning: "", afternoon: "", closed: false },
  { day: "Jeudi", morning: "", afternoon: "", closed: false },
  { day: "Vendredi", morning: "", afternoon: "", closed: false },
  { day: "Samedi", morning: "", afternoon: "", closed: true },
  { day: "Dimanche", morning: "", afternoon: "", closed: true },
];

export default function SettingsAdmin() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (settings) {
      setS({
        ...settings,
        hours: (settings.hours && settings.hours.length) ? settings.hours : DEFAULT_HOURS,
        social: settings.social || {},
        hero_kpis: settings.hero_kpis || [],
        why_choose_us: settings.why_choose_us || [],
        brands: settings.brands || [],
        intervention_postal_codes: settings.intervention_postal_codes || [],
        intervention_departments: settings.intervention_departments || [],
        contact_icons: settings.contact_icons || { phone: "Phone", email: "Mail", address: "MapPin", hours: "Clock" },
        cta_icons: settings.cta_icons || { phone: "Phone", devis: "FileText", rappel: "PhoneCall", message: "MessageSquare" },
        differentiation_badge_icons: settings.differentiation_badge_icons || ["Flame", "Droplets"],
      });
    }
  }, [settings]);

  if (!s) return <div className="p-10">Chargement...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      await refresh();
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const r = await api.post("/template/reset", {});
      toast.success(r.data?.message || "Template réinitialisé");
      await refresh();
      setResetOpen(false);
      setResetConfirm("");
      // Full reload so frontend re-reads everything
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error("Échec : " + (e?.response?.data?.detail || e.message));
    } finally {
      setResetting(false);
    }
  };

  // --- Helpers pour listes dynamiques ---
  const addItem = (key, item) => setS({ ...s, [key]: [...(s[key] || []), item] });
  const updItem = (key, i, patch) => {
    const arr = [...(s[key] || [])];
    arr[i] = { ...arr[i], ...patch };
    setS({ ...s, [key]: arr });
  };
  const delItem = (key, i) => {
    const arr = [...(s[key] || [])];
    arr.splice(i, 1);
    setS({ ...s, [key]: arr });
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-settings-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Configuration</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Paramètres du site</h1>
          {s.is_template && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold">
              <AlertTriangle className="w-3.5 h-3.5" />
              Mode TEMPLATE VIERGE actif — remplissez tous les champs marqués <code className="font-mono">[...]</code>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => setResetOpen(true)}
          data-testid="open-reset-template-btn"
        >
          <RefreshCcw className="w-4 h-4 mr-1.5" />
          Réinitialiser en mode template vierge
        </Button>
      </header>

      {/* --- IDENTITÉ --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Identité</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nom de l'entreprise</Label><Input value={s.company_name||""} onChange={e=>setS({...s, company_name: e.target.value})} data-testid="settings-company-name"/></div>
          <div><Label>Nom court (header)</Label><Input value={s.company_short_name||""} onChange={e=>setS({...s, company_short_name: e.target.value})} placeholder="ex: MonEntreprise"/></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Secteur d'activité</Label><Input value={s.sector||""} onChange={e=>setS({...s, sector: e.target.value})}/></div>
          <div><Label>Baseline (sous le nom)</Label><Input value={s.company_baseline||""} onChange={e=>setS({...s, company_baseline: e.target.value})} placeholder="Plomberie · Climatisation"/></div>
        </div>
        <div><Label>Tagline courte</Label><Input value={s.tagline||""} onChange={e=>setS({...s, tagline: e.target.value})}/></div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Texte de présentation (pied de page)</Label>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))] tabular-nums">{(s.footer_description||"").length} caractères</span>
          </div>
          <Textarea rows={6} value={s.footer_description||""} onChange={e=>setS({...s, footer_description: e.target.value})} data-testid="settings-footer-description"/>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Idéal : 300–500 caractères. Les retours à la ligne sont conservés.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Logo de l'entreprise</Label>
            <ImageUploader folder="logos" label="Uploader un logo" onUploaded={(url) => setS({ ...s, logo_url: url })} />
          </div>
          <Input value={s.logo_url||""} onChange={e=>setS({...s, logo_url: e.target.value})} placeholder="URL ou /uploads/cms/logos/..."/>
          {s.logo_url ? (
            <div className="mt-2 p-3 rounded-xl bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] inline-block">
              <img src={s.logo_url} alt="Logo" className="h-16 w-auto"/>
            </div>
          ) : (
            <div className="mt-2 p-6 rounded-xl bg-[hsl(var(--muted))]/40 border-2 border-dashed border-[hsl(var(--border))] inline-flex items-center justify-center">
              <span className="font-display font-black text-2xl tracking-widest text-[hsl(var(--muted-foreground))]">VOTRE LOGO</span>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Couleur principale</Label><Input type="color" value={s.primary_color||"#1E3A5F"} onChange={e=>setS({...s, primary_color: e.target.value})} className="h-11"/></div>
          <div><Label>Couleur secondaire</Label><Input type="color" value={s.secondary_color||"#3DB9E8"} onChange={e=>setS({...s, secondary_color: e.target.value})} className="h-11"/></div>
          <div><Label>Couleur accent</Label><Input type="color" value={s.accent_color||"#E30613"} onChange={e=>setS({...s, accent_color: e.target.value})} className="h-11"/></div>
        </div>
      </Card>

      {/* --- DIRIGEANT / À PROPOS --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Dirigeant &amp; À propos</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nom du dirigeant</Label><Input value={s.owner_name||""} onChange={e=>setS({...s, owner_name: e.target.value})} placeholder="Prénom Nom"/></div>
          <div><Label>Titre / Fonction</Label><Input value={s.owner_title||""} onChange={e=>setS({...s, owner_title: e.target.value})} placeholder="Gérant fondateur"/></div>
        </div>
        <div><Label>Biographie du dirigeant</Label><Textarea rows={3} value={s.owner_bio||""} onChange={e=>setS({...s, owner_bio: e.target.value})}/></div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Photo / Avatar du dirigeant</Label>
            <ImageUploader folder="owners" label="Uploader un avatar" onUploaded={(url) => setS({ ...s, owner_avatar_url: url })} />
          </div>
          <Input value={s.owner_avatar_url||""} onChange={e=>setS({...s, owner_avatar_url: e.target.value})} placeholder="URL de la photo du dirigeant"/>
          {s.owner_avatar_url && <img src={s.owner_avatar_url} alt="Avatar" className="mt-2 h-20 w-20 rounded-full object-cover border-2 border-[hsl(var(--border))]"/>}
        </div>
        <div className="border-t pt-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Titre section "À propos"</Label><Input value={s.about_title||""} onChange={e=>setS({...s, about_title: e.target.value})}/></div>
            <div><Label>Sous-titre "À propos"</Label><Input value={s.about_subtitle||""} onChange={e=>setS({...s, about_subtitle: e.target.value})}/></div>
          </div>
          <div><Label>Texte complet section "À propos"</Label><Textarea rows={4} value={s.about_text||""} onChange={e=>setS({...s, about_text: e.target.value})}/></div>
        </div>
      </Card>

      {/* --- HERO --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Hero (page d'accueil)</h3>
        <div><Label>Titre H1</Label><Input value={s.hero_title||""} onChange={e=>setS({...s, hero_title: e.target.value})}/></div>
        <div><Label>Sous-titre</Label><Textarea rows={2} value={s.hero_subtitle||""} onChange={e=>setS({...s, hero_subtitle: e.target.value})}/></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Type arrière-plan</Label>
            <Select value={s.hero_bg_type||"image"} onValueChange={v=>setS({...s, hero_bg_type: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="carousel">Carrousel</SelectItem><SelectItem value="youtube">Vidéo YouTube</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>YouTube ID (arrière-plan hero si vidéo)</Label><Input value={s.hero_youtube||""} onChange={e=>setS({...s, hero_youtube: e.target.value})}/></div>
        </div>
        <div><Label>Vidéo de présentation (bulle ronde) — YouTube ID</Label><Input value={s.presentation_video_youtube||""} onChange={e=>setS({...s, presentation_video_youtube: e.target.value})} placeholder="ex: dQw4w9WgXcQ"/></div>
        <div><Label>Images hero (une URL par ligne)</Label><Textarea rows={3} value={(s.hero_bg_images||[]).join("\n")} onChange={e=>setS({...s, hero_bg_images: e.target.value.split("\n").filter(Boolean)})}/></div>

        {/* Hero KPIs */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>KPIs du hero (badges chiffres)</Label>
            <Button type="button" size="sm" variant="outline" onClick={() => addItem("hero_kpis", {icon: "Award", label: "", desc: ""})}><Plus className="w-3.5 h-3.5 mr-1"/>Ajouter</Button>
          </div>
          {(s.hero_kpis||[]).map((k, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 mb-2 items-center">
              <IconPicker value={k.icon || "Award"} onChange={(v) => updItem("hero_kpis", i, {icon: v})} size="sm"/>
              <Input placeholder="ex: 10 ans" value={k.label||""} onChange={e=>updItem("hero_kpis", i, {label: e.target.value})}/>
              <Input placeholder="ex: d'expérience" value={k.desc||""} onChange={e=>updItem("hero_kpis", i, {desc: e.target.value})}/>
              <Button type="button" size="sm" variant="ghost" onClick={() => delItem("hero_kpis", i)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
            </div>
          ))}
          {(s.hero_kpis||[]).length === 0 && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Aucun KPI. Les 4 KPIs par défaut s'afficheront (années, disponibilité, rayon, garantie).</p>}
        </div>
      </Card>

      {/* --- POURQUOI NOUS CHOISIR --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Section "Pourquoi nous choisir"</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Sur-titre (kicker)</Label><Input value={s.differentiation_kicker||""} onChange={e=>setS({...s, differentiation_kicker: e.target.value})}/></div>
          <div><Label>Badge services (3 mots)</Label><Input value={s.differentiation_badge_text||""} onChange={e=>setS({...s, differentiation_badge_text: e.target.value})}/></div>
        </div>
        <div><Label>Titre principal</Label><Input value={s.differentiation_title||""} onChange={e=>setS({...s, differentiation_title: e.target.value})}/></div>
        <div><Label>Texte principal</Label><Textarea rows={3} value={s.differentiation_text||""} onChange={e=>setS({...s, differentiation_text: e.target.value})}/></div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>Atouts / Raisons de nous choisir</Label>
            <Button type="button" size="sm" variant="outline" onClick={() => addItem("why_choose_us", {icon: "Shield", title: "", desc: ""})}><Plus className="w-3.5 h-3.5 mr-1"/>Ajouter</Button>
          </div>
          {(s.why_choose_us||[]).map((w, i) => (
            <div key={i} className="p-3 rounded-lg bg-[hsl(var(--muted))]/40 mb-2 space-y-2">
              <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
                <IconPicker value={w.icon || "Shield"} onChange={(v) => updItem("why_choose_us", i, {icon: v})} size="sm"/>
                <Input placeholder="Titre de l'atout" value={w.title||""} onChange={e=>updItem("why_choose_us", i, {title: e.target.value})}/>
                <Button type="button" size="sm" variant="ghost" onClick={() => delItem("why_choose_us", i)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
              </div>
              <Input placeholder="Description courte" value={w.desc||""} onChange={e=>updItem("why_choose_us", i, {desc: e.target.value})}/>
            </div>
          ))}
          {(s.why_choose_us||[]).length === 0 && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Aucun atout. Les 4 atouts par défaut s'afficheront.</p>}
        </div>
      </Card>

      {/* --- MARQUES --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Marques installées / Partenaires</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Sur-titre</Label><Input value={s.brands_kicker||""} onChange={e=>setS({...s, brands_kicker: e.target.value})}/></div>
          <div><Label>Titre</Label><Input value={s.brands_title||""} onChange={e=>setS({...s, brands_title: e.target.value})}/></div>
        </div>
        <div className="flex items-center justify-between">
          <Label>Liste des marques</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => addItem("brands", {name: "", logo_url: "", website: "", description: ""})}><Plus className="w-3.5 h-3.5 mr-1"/>Ajouter</Button>
        </div>
        {(s.brands||[]).map((b, i) => (
          <div key={i} className="p-3 rounded-lg bg-[hsl(var(--muted))]/40 space-y-2">
            <div className="grid md:grid-cols-[1fr_auto] gap-2 items-center">
              <Input placeholder="Nom de la marque" value={b.name||""} onChange={e=>updItem("brands", i, {name: e.target.value})}/>
              <div className="flex items-center gap-2">
                <ImageUploader folder="brands" label="Logo" onUploaded={(url) => updItem("brands", i, {logo_url: url})} />
                <Button type="button" size="sm" variant="ghost" onClick={() => delItem("brands", i)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
              </div>
            </div>
            <Input placeholder="URL du logo" value={b.logo_url||""} onChange={e=>updItem("brands", i, {logo_url: e.target.value})}/>
            <div className="grid md:grid-cols-2 gap-2">
              <Input placeholder="Site web (optionnel)" value={b.website||""} onChange={e=>updItem("brands", i, {website: e.target.value})}/>
              <Input placeholder="Description courte" value={b.description||""} onChange={e=>updItem("brands", i, {description: e.target.value})}/>
            </div>
            {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-10 w-auto object-contain"/>}
          </div>
        ))}
      </Card>

      {/* --- ZONE D'INTERVENTION --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Zone géographique d'intervention</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Ville principale</Label><Input value={s.main_city||""} onChange={e=>setS({...s, main_city: e.target.value})} placeholder="ex: Grans"/></div>
          <div><Label>Rayon (km)</Label><Input type="number" value={s.intervention_radius_km||40} onChange={e=>setS({...s, intervention_radius_km: +e.target.value})}/></div>
          <div><Label>Départements (virgules)</Label>
            <Input value={(s.intervention_departments||[]).join(", ")} onChange={e=>setS({...s, intervention_departments: e.target.value.split(",").map(x=>x.trim()).filter(Boolean)})} placeholder="Bouches-du-Rhône, Vaucluse"/>
          </div>
        </div>
        <div>
          <Label>Codes postaux couverts (un par ligne, format : "13300 Salon-de-Provence")</Label>
          <Textarea
            rows={4}
            placeholder="13450 Grans&#10;13300 Salon-de-Provence&#10;13800 Istres"
            value={(s.intervention_postal_codes||[]).map(z => `${z.code||""} ${z.city||""}`.trim()).join("\n")}
            onChange={e => {
              const items = e.target.value.split("\n").map(line => {
                const m = line.trim().match(/^(\d{5})\s+(.+)$/);
                if (m) return { code: m[1], city: m[2].trim() };
                const code = line.trim().match(/^\d{5}$/);
                if (code) return { code: code[0], city: "" };
                if (line.trim()) return { code: "", city: line.trim() };
                return null;
              }).filter(Boolean);
              setS({...s, intervention_postal_codes: items});
            }}
          />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Phase 2 : ces codes postaux alimenteront automatiquement les pages SEO "service × ville". Pour l'instant, ils sont juste stockés.</p>
        </div>
      </Card>

      {/* --- AVIS GOOGLE --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Avis Google</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Nombre d'avis</Label><Input type="number" value={s.reviews_count||0} onChange={e=>setS({...s, reviews_count: +e.target.value})}/></div>
          <div><Label>Note moyenne</Label><Input type="number" step="0.1" value={s.reviews_average||5} onChange={e=>setS({...s, reviews_average: +e.target.value})}/></div>
          <div><Label>Depuis (année)</Label><Input type="number" value={s.reviews_since_year||new Date().getFullYear()} onChange={e=>setS({...s, reviews_since_year: +e.target.value})}/></div>
        </div>
        <div><Label>Lien Google "Laisser un avis"</Label>
          <Input value={s.google_review_url||""} onChange={e=>setS({...s, google_review_url: e.target.value})} placeholder="https://g.page/r/XXXXXXXXX/review"/>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Collez le lien court fourni par Google Business Profile (format <code className="font-mono">g.page/r/.../review</code>).</p>
        </div>
        <div>
          <Label>Témoignages (un par ligne : nom | ville | texte | date)</Label>
          <Textarea
            rows={5}
            placeholder="Sophie M. | Salon-de-Provence | Travail impeccable | il y a 2 mois"
            value={(s.testimonials||[]).map(t => `${t.name||""} | ${t.city||""} | ${t.text||""} | ${t.date||""}`).join("\n")}
            onChange={e => {
              const items = e.target.value.split("\n").filter(Boolean).map(line => {
                const [name, city, text, date] = line.split("|").map(x=>x.trim());
                return { name: name||"", city: city||"", text: text||"", date: date||"", rating: 5 };
              });
              setS({...s, testimonials: items});
            }}
          />
        </div>
      </Card>

      {/* --- CONTACT --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Contact &amp; adresse</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Téléphone</Label><Input value={s.phone||""} onChange={e=>setS({...s, phone: e.target.value})}/></div>
          <div><Label>Email</Label><Input value={s.email||""} onChange={e=>setS({...s, email: e.target.value})}/></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Adresse publique</Label><Input value={s.address_public||""} onChange={e=>setS({...s, address_public: e.target.value})}/></div>
          <div><Label>Siège social</Label><Input value={s.address_headquarters||""} onChange={e=>setS({...s, address_headquarters: e.target.value})}/></div>
          <div><Label>RDV</Label><Input value={s.address_appointment||""} onChange={e=>setS({...s, address_appointment: e.target.value})}/></div>
        </div>
      </Card>

      {/* --- HORAIRES --- */}
      <Card className="p-6 border mb-4 space-y-3">
        <h3 className="font-display font-bold">Horaires</h3>
        {(s.hours||[]).map((h,i)=>(
          <div key={i} className="grid grid-cols-[100px_1fr_1fr_auto] gap-2 items-center">
            <span className="text-sm font-semibold">{h.day}</span>
            <Input value={h.morning||""} onChange={e=>{const hours=[...s.hours]; hours[i]={...h, morning: e.target.value}; setS({...s, hours});}} placeholder="08:00-12:00"/>
            <Input value={h.afternoon||""} onChange={e=>{const hours=[...s.hours]; hours[i]={...h, afternoon: e.target.value}; setS({...s, hours});}} placeholder="14:00-18:00"/>
            <div className="flex items-center gap-2"><Switch checked={!h.closed} onCheckedChange={v=>{const hours=[...s.hours]; hours[i]={...h, closed: !v}; setS({...s, hours});}}/><span className="text-xs">{h.closed?"Fermé":"Ouvert"}</span></div>
          </div>
        ))}
      </Card>

      {/* --- RÉSEAUX --- */}
      <Card className="p-6 border mb-4 space-y-3">
        <h3 className="font-display font-bold">Réseaux sociaux</h3>
        {["facebook","instagram","linkedin","youtube"].map(k => (
          <div key={k}><Label className="capitalize">{k}</Label><Input value={s.social?.[k]||""} onChange={e=>setS({...s, social:{...s.social, [k]: e.target.value}})} placeholder={`URL ${k}`}/></div>
        ))}
      </Card>

      {/* --- ICÔNES GLOBALES --- */}
      <Card className="p-6 border mb-4 space-y-5">
        <div>
          <h3 className="font-display font-bold">Iconographie du site</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Personnalisez toutes les icônes affichées sur votre site public. Plus de 100 icônes disponibles, recherche par mot-clé et catégorie.</p>
        </div>

        {/* Boutons CTA du header */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--brand-navy)] mb-2">Boutons CTA (header &amp; actions flottantes)</div>
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { key: "phone", label: "Appel" },
              { key: "devis", label: "Devis" },
              { key: "rappel", label: "Rappel" },
              { key: "message", label: "Message" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--muted))]/40">
                <IconPicker
                  value={s.cta_icons?.[key] || ""}
                  onChange={(v) => setS({ ...s, cta_icons: { ...(s.cta_icons || {}), [key]: v } })}
                  allowEmpty
                />
                <div>
                  <div className="text-sm font-semibold">{label}</div>
                  <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{s.cta_icons?.[key] || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Icônes page Contact */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--brand-navy)] mb-2">Page Contact (cartes téléphone, email, adresse, horaires)</div>
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { key: "phone", label: "Téléphone" },
              { key: "email", label: "Email" },
              { key: "address", label: "Adresse" },
              { key: "hours", label: "Horaires" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--muted))]/40">
                <IconPicker
                  value={s.contact_icons?.[key] || ""}
                  onChange={(v) => setS({ ...s, contact_icons: { ...(s.contact_icons || {}), [key]: v } })}
                />
                <div>
                  <div className="text-sm font-semibold">{label}</div>
                  <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{s.contact_icons?.[key] || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Badge différenciation */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--brand-navy)] mb-2">Badge section "Pourquoi nous" (mini icônes à gauche du badge)</div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--muted))]/40">
            {(s.differentiation_badge_icons || []).map((ic, i) => (
              <div key={i} className="flex items-center gap-2">
                <IconPicker
                  value={ic}
                  onChange={(v) => {
                    const arr = [...(s.differentiation_badge_icons || [])];
                    arr[i] = v;
                    setS({ ...s, differentiation_badge_icons: arr });
                  }}
                  allowEmpty
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    const arr = [...(s.differentiation_badge_icons || [])];
                    arr.splice(i, 1);
                    setS({ ...s, differentiation_badge_icons: arr });
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </Button>
              </div>
            ))}
            {(s.differentiation_badge_icons || []).length < 4 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setS({ ...s, differentiation_badge_icons: [...(s.differentiation_badge_icons || []), "Sparkles"] })}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />Ajouter
              </Button>
            )}
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Ces icônes apparaissent en mini-pastilles superposées à gauche du texte du badge dans la section différenciation.</p>
        </div>

        <div className="text-[11px] text-[hsl(var(--muted-foreground))] bg-blue-50 border border-blue-200 rounded-lg p-3">
          💡 <strong>Astuce</strong> : les icônes individuelles des <strong>services</strong>, des <strong>KPIs du hero</strong> et des <strong>atouts "pourquoi nous choisir"</strong> se configurent directement dans leurs sections respectives (plus haut dans cette page pour les KPIs et atouts, et dans le menu <em>Services</em> pour chaque service).
        </div>
      </Card>

      {/* --- MENTIONS LÉGALES --- */}
      <Card className="p-6 border mb-4 space-y-3">
        <h3 className="font-display font-bold">Mentions légales / Footer</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Raison sociale complète</Label><Input value={s.footer_legal_company||""} onChange={e=>setS({...s, footer_legal_company: e.target.value})} placeholder="ex: SARL MonEntreprise"/></div>
          <div><Label>SIRET</Label><Input value={s.footer_legal_siret||""} onChange={e=>setS({...s, footer_legal_siret: e.target.value})} placeholder="SIRET : 12345678900012"/></div>
        </div>
        <div><Label>Certifications / RGE</Label><Input value={s.footer_legal_rge||""} onChange={e=>setS({...s, footer_legal_rge: e.target.value})} placeholder="Certifié RGE QualiPAC, Qualibat"/></div>
        <div><Label>Informations complémentaires</Label><Textarea rows={2} value={s.footer_legal_extra||""} onChange={e=>setS({...s, footer_legal_extra: e.target.value})}/></div>
      </Card>

      {/* --- SMTP --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">SMTP — Envoi des leads par email</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-2">Configurez votre serveur SMTP (PlanetHoster, OVH, Gmail, etc.) pour que chaque demande soit envoyée par email.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Serveur SMTP</Label><Input value={s.smtp_host||""} onChange={e=>setS({...s, smtp_host: e.target.value})} placeholder="mail.votre-domaine.fr"/></div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <div><Label>Port</Label><Input type="number" value={s.smtp_port||465} onChange={e=>setS({...s, smtp_port: +e.target.value})}/></div>
            <div><Label>SSL</Label><div className="flex items-center h-10 gap-2"><Switch checked={s.smtp_use_ssl !== false} onCheckedChange={v=>setS({...s, smtp_use_ssl: v})}/></div></div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Identifiant</Label><Input value={s.smtp_user||""} onChange={e=>setS({...s, smtp_user: e.target.value})} placeholder="noreply@votre-domaine.fr"/></div>
          <div><Label>Mot de passe</Label><Input type="password" value={s.smtp_password||""} onChange={e=>setS({...s, smtp_password: e.target.value})} placeholder="••••••••"/></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nom expéditeur</Label><Input value={s.smtp_from_name||""} onChange={e=>setS({...s, smtp_from_name: e.target.value})}/></div>
          <div><Label>Email expéditeur (From)</Label><Input value={s.smtp_from_email||""} onChange={e=>setS({...s, smtp_from_email: e.target.value})}/></div>
        </div>
        <div><Label>Destinataire des notifications</Label><Input value={s.notification_email||""} onChange={e=>setS({...s, notification_email: e.target.value})}/></div>
        <div className="flex items-center gap-3 pt-2">
          <Button type="button" variant="outline" onClick={async()=>{
            try {
              const r = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/test-smtp`, {
                method: "POST",
                headers: {"Content-Type":"application/json","Authorization":`Bearer ${localStorage.getItem("kap_token")}`},
                body: JSON.stringify({})
              })).json();
              if (r.sent) toast.success("Email de test envoyé — vérifiez " + (s.notification_email||"votre boîte"));
              else toast.error("Échec SMTP : " + (r.error || r.reason || "inconnu"));
            } catch (e) { toast.error("Erreur : " + e.message); }
          }} data-testid="test-smtp-btn">Envoyer un email de test</Button>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">Enregistrez avant de tester.</span>
        </div>
      </Card>

      {/* --- ANTI-SPAM --- */}
      <Card className="p-6 border mb-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold">Sécurité des formulaires (anti-bot / anti-spam)</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Plusieurs couches de protection gratuites : honeypot, délai minimum, limitation par IP, captcha maths. Aucun service tiers requis.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">Activer</span>
            <Switch
              checked={s.antispam_enabled !== false}
              onCheckedChange={v => setS({ ...s, antispam_enabled: v })}
              data-testid="antispam-toggle"
            />
          </div>
        </div>

        <div className={`grid md:grid-cols-3 gap-3 ${s.antispam_enabled === false ? "opacity-40 pointer-events-none" : ""}`}>
          <div>
            <Label>Délai minimum de saisie (sec)</Label>
            <Input
              type="number" min="0" max="60"
              value={s.antispam_min_seconds ?? 3}
              onChange={e => setS({ ...s, antispam_min_seconds: +e.target.value })}
              data-testid="antispam-min-seconds"
            />
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Rejette les soumissions trop rapides (bots).</p>
          </div>
          <div>
            <Label>Max envois / heure / IP</Label>
            <Input
              type="number" min="0" max="100"
              value={s.antispam_rate_per_hour ?? 5}
              onChange={e => setS({ ...s, antispam_rate_per_hour: +e.target.value })}
              data-testid="antispam-rate-limit"
            />
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">0 = pas de limitation.</p>
          </div>
          <div>
            <Label>Nom du champ honeypot</Label>
            <Input
              value={s.antispam_honeypot_field || "website"}
              onChange={e => setS({ ...s, antispam_honeypot_field: e.target.value })}
              data-testid="antispam-honeypot-field"
            />
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Champ invisible piégé.</p>
          </div>
        </div>

        <div className={`flex items-center gap-3 ${s.antispam_enabled === false ? "opacity-40 pointer-events-none" : ""}`}>
          <Switch
            checked={!!s.antispam_math_captcha}
            onCheckedChange={v => setS({ ...s, antispam_math_captcha: v })}
            data-testid="antispam-math-captcha"
          />
          <div>
            <div className="text-sm font-semibold">Activer le captcha mathématique</div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Ajoute une question "3 + 4 = ?" au formulaire. Token signé 10 min.</p>
          </div>
        </div>

        <div className={s.antispam_enabled === false ? "opacity-40 pointer-events-none" : ""}>
          <Label>Mots-clés bloqués (séparés par virgules)</Label>
          <Textarea
            rows={2}
            value={(s.antispam_blocked_keywords || []).join(", ")}
            onChange={e => setS({ ...s, antispam_blocked_keywords: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })}
            placeholder="http://, viagra, bitcoin, backlink..."
            data-testid="antispam-blocked-keywords"
          />
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
            Toute soumission contenant l'un de ces mots-clés est rejetée et loggée dans l'audit spam.
          </p>
        </div>
      </Card>

      {/* --- CLICK & COLLECT (accès rapide) --- */}
      {s.shop_enabled && (
        <Card className="p-6 border mb-4 space-y-3" data-testid="click-collect-settings-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold">Click & Collect</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Activez le retrait en magasin. La configuration détaillée (points de retrait, créneaux, préparateurs) se fait dans l'onglet dédié <em>Click & Collect</em>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold">Activer</span>
              <Switch
                checked={!!s.shop_click_collect_enabled}
                onCheckedChange={v => setS({ ...s, shop_click_collect_enabled: v })}
                data-testid="click-collect-toggle"
              />
            </div>
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
            <strong>{(s.pickup_locations || []).filter(l => l.active !== false).length}</strong> point(s) de retrait actifs — <strong>{s.pickup_slot_duration_min || 30} min</strong> par créneau · capacité <strong>{s.pickup_capacity_per_slot || 5}</strong>/créneau · fenêtre <strong>{s.pickup_max_days_ahead || 14} jours</strong>.
          </div>
        </Card>
      )}

      <div className="sticky bottom-0 py-4 bg-gradient-to-t from-[hsl(var(--background))] to-transparent">
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 w-full md:w-auto" data-testid="save-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1"/>Enregistrer tous les paramètres</>}</Button>
      </div>

      {/* --- MODALE RESET TEMPLATE --- */}
      {resetOpen && (
        <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" onClick={() => !resetting && setResetOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4" onClick={e=>e.stopPropagation()} data-testid="reset-template-modal">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600"/>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-[color:var(--brand-navy)]">Réinitialiser en mode template vierge</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Cette action <strong>supprime tous les contenus</strong> (services, réalisations, blog, FAQ, zones, paramètres, liens courts, analytics) et les remplace par des placeholders français.</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 space-y-1">
              <div className="font-semibold">Sera conservé :</div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Comptes utilisateurs (admin &amp; client)</li>
                <li>Fichiers uploadés dans la médiathèque</li>
              </ul>
              <div className="font-semibold pt-2">Sera supprimé :</div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Tous les paramètres entreprise (logo, couleurs, contact…)</li>
                <li>Tous les services, réalisations, articles, FAQ, zones</li>
                <li>Tous les leads, événements analytics, liens courts</li>
              </ul>
            </div>
            <div>
              <Label className="text-sm">Pour confirmer, tapez <code className="font-mono bg-red-50 px-1.5 py-0.5 rounded text-red-700 font-bold">RESET</code> ci-dessous :</Label>
              <Input
                value={resetConfirm}
                onChange={e => setResetConfirm(e.target.value)}
                className="mt-1.5"
                placeholder="RESET"
                data-testid="reset-template-confirm-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setResetOpen(false); setResetConfirm(""); }} disabled={resetting}>Annuler</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={doReset}
                disabled={resetting || resetConfirm !== "RESET"}
                data-testid="reset-template-confirm-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <RefreshCcw className="w-4 h-4 mr-1"/>}
                Réinitialiser maintenant
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
